import React, {useEffect, forwardRef} from 'react';
import cx from 'classnames';
import isEmpty from 'lodash.isempty';
import newsActions from '../../actions/news.actions';
import {Message} from 'epic-diesel-common';
import {PageNav} from '../../components';
import {formatDateByLocale} from '../../utils/Utils';
import {ArticleViewProps} from './ArticleView';
import {NewsState} from '../../reducers/news.reducer';
import {useDispatch} from 'react-redux';

//@ts-ignore
import styles from './Article.pcss';

export interface ArticleProps extends ArticleViewProps {
    newsState: NewsState;
}

const CODES = {
    header: 'Article',
    author: 'epic.affiliate.news.article.author',
    pageNav: 'epic.affiliate.news.backTo',
    tags: 'epic.affiliate.common.tags'
};

const Article = forwardRef<HTMLTableSectionElement, ArticleProps>(
    ({newsState, match}, forwardRef) => {
        const dispatch = useDispatch();
        const {post} = newsState || {};

        useEffect(() => {
            const {params} = match || {};
            const {locale, slug} = params || {};
            dispatch(newsActions.getArticle({slug, locale}));
        }, []);

        if (isEmpty(post)) {
            return null;
        }

        const {
            trendingImage,
            trendingImageAlt = '',
            content,
            title,
            subtitle,
            author,
            slug,
            tags
        } = post;

        const date = formatDateByLocale(post.date);
        const noTopImage = post.noTopImage === true || !trendingImage;
        const tagsArr = (tags && tags.split(',')) || [];

        const style = !noTopImage && {
            backgroundImage: `url("${trendingImage}")`,
            backgroundPosition: 'top'
        };

        return (
            <>
                <PageNav
                    to="news"
                    returnText={<Message code={CODES.pageNav} />}
                    title={post.title}
                />

                <section ref={forwardRef}>
                    {!noTopImage && (
                        <div className={styles.articleBanner} style={style}>
                            <img
                                className={styles.invisibleImage}
                                alt={trendingImageAlt}
                                src={trendingImage}
                            />
                        </div>
                    )}

                    <article
                        className={cx(styles.articleContainer, {[styles.noTopImage]: noTopImage})}>
                        <div className={styles.headerInfo}>
                            <h1>{title}</h1>

                            <div className={styles.meta}>
                                {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
                                {date && <span>{date}</span>}
                            </div>

                            {author && (
                                <div className={styles.author}>
                                    <Message code={CODES.author} args={[author]} />
                                </div>
                            )}
                        </div>

                        <div
                            className={styles.cmsContent}
                            dangerouslySetInnerHTML={{__html: content}}
                        />

                        {tagsArr.length > 0 && (
                            <ul className={styles.tags}>
                                <li>
                                    <Message code={CODES.tags} args={[': ']} />
                                </li>
                                {tagsArr.map(tag => (
                                    <li key={tag} className={styles.tag} id={slug}>
                                        <span>{tag}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </article>
                </section>
            </>
        );
    }
);

export default Article;
